import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { EmptyStateCard } from '@/components/ui/EmptyStateCard';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import DonutChart from '@/components/ui/DonutChart';
import BarChart from '@/components/ui/BarChart';
import MetricCard from '@/components/ui/MetricCard';
import PartnerCard from '@/components/ui/PartnerCard';
import TechniqueCard from '@/components/ui/TechniqueCard';
import ScheduleStatusCard from '@/components/ui/ScheduleStatusCard';
import AchievementCard from '@/components/ui/AchievementCard';
import { usePractitionerHighlights, PractitionerHighlight } from '@/hooks/usePractitionerHighlights';
import { Plus, Users, Star, Clock, Target, Trophy, MessageCircle, UserCheck, Award, UserPlus, BarChart3 } from 'lucide-react';

const PractitionerJourneyHighlightsSection: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const queryClient = useQueryClient();
  const { highlights: highlightsData, isLoading, error, refetch } = usePractitionerHighlights();

  // Force reload when user returns to Home page
  useEffect(() => {
    if (location.pathname === '/home') {
      queryClient.invalidateQueries({ queryKey: ['practitioner-highlights'] });
      refetch();
    }
  }, [location.pathname, queryClient, refetch]);

  // Calculate total number of pages based on visible cards
  const cardsPerView = 3;
  const totalCards = highlightsData ? 11 : 0; // 11 cards total
  const totalPages = Math.ceil(totalCards / cardsPerView);

  // Handle scroll to update current index
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const cardWidth = 135;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setCurrentIndex(Math.min(newIndex, totalPages - 1));
    }
  };

  // Scroll to specific page
  const scrollToPage = (pageIndex: number) => {
    if (scrollContainerRef.current) {
      const scrollLeft = pageIndex * cardWidth * cardsPerView;
      scrollContainerRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const cardWidth = 135;

  if (isLoading) {
    return (
      <div className="mb-8">
        <h2 className="text-sm font-bold text-white font-roboto mb-4">
          {t('home.journey_highlights.title')}
        </h2>
        <div className="text-gray-400">Loading highlights...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <h2 className="text-sm font-bold text-white font-roboto mb-4">
          {t('home.journey_highlights.title')}
        </h2>
        <div className="text-red-400">Error loading highlights</div>
      </div>
    );
  }

  if (!highlightsData) {
    return null;
  }

  // Create highlights array from data
  const highlights: PractitionerHighlight[] = [
    {
      id: '1',
      type: 'total_trainings',
      title: 'Total Trainings',
      value: highlightsData.totalTrainings.total,
      description: `${highlightsData.totalTrainings.group} Group + ${highlightsData.totalTrainings.private} Private`,
      icon: '🥋'
    },
    {
      id: '2',
      type: 'new_connections',
      title: 'New Connections',
      value: highlightsData.newConnections,
      description: 'This month',
      icon: '🤝'
    },
    {
      id: '3',
      type: 'most_frequent_partner',
      title: 'Most Frequent Partner',
      value: highlightsData.mostFrequentPartner?.name || 'No partners yet',
      description: highlightsData.mostFrequentPartner ? `${highlightsData.mostFrequentPartner.trainingCount} trainings` : 'Add partners to see',
      icon: '👥',
      partnerInfo: highlightsData.mostFrequentPartner
    },
    {
      id: '4',
      type: 'average_rating',
      title: 'Average Rating',
      value: highlightsData.averageRating.toFixed(1),
      description: 'Your performance',
      icon: '⭐'
    },
    {
      id: '5',
      type: 'total_hours',
      title: 'Total Hours',
      value: `${Math.round(highlightsData.totalHours)}h`,
      description: 'On the mat',
      icon: '🕐'
    },
    {
      id: '6',
      type: 'most_applied_technique',
      title: 'Most Applied Technique',
      value: highlightsData.mostAppliedTechnique?.name || 'No techniques yet',
      description: highlightsData.mostAppliedTechnique ? `${highlightsData.mostAppliedTechnique.count} times` : 'Link techniques to see',
      icon: '🥊',
      techniqueInfo: highlightsData.mostAppliedTechnique
    },
    {
      id: '7',
      type: 'schedule_status',
      title: 'Schedule Status',
      value: `${highlightsData.scheduleStatus.completedPercentage}%`,
      description: `${highlightsData.scheduleStatus.completed} completed, ${highlightsData.scheduleStatus.missed} missed`,
      icon: '📊',
      scheduleStatusData: {
        completed: highlightsData.scheduleStatus.completed,
        missed: highlightsData.scheduleStatus.missed,
        total: highlightsData.scheduleStatus.completed + highlightsData.scheduleStatus.missed,
        completedPercentage: highlightsData.scheduleStatus.completedPercentage,
        missedPercentage: highlightsData.scheduleStatus.missedPercentage
      }
    },
    {
      id: '8',
      type: 'recognitions',
      title: 'Recognitions',
      value: highlightsData.recognitions,
      description: highlightsData.recognitions > 0 ? 'Your hard work is being recognized!' : 'Train with partners to see your first recognition here.',
      icon: '🏆'
    },
    {
      id: '9',
      type: 'unique_partners',
      title: 'Partners you\'ve rolled',
      value: highlightsData.uniquePartners,
      description: highlightsData.uniquePartners > 0 ? 'More partners, more rolls, more growth.' : 'Log your first roll with a partner to see them here.',
      icon: '👥'
    },
    {
      id: '10',
      type: 'interactions',
      title: 'Interactions',
      value: highlightsData.interactions,
      description: highlightsData.interactions > 0 ? 'Great job connecting with the community!' : 'The mat is ready for your first post or comment!',
      icon: '💬'
    },
    {
      id: '11',
      type: 'achievement',
      title: 'Achievement',
      value: highlightsData.achievement?.title || 'No milestone yet',
      description: highlightsData.achievement?.description || 'Complete trainings to unlock',
      icon: highlightsData.achievement?.emoji || '🎯',
      achievementInfo: highlightsData.achievement
    }
  ];

  const renderCard = (highlight: PractitionerHighlight) => {
    const isEmpty = highlight.value === 0 || highlight.value === '0' || highlight.value === '0.0' || highlight.value === 'No partners yet' || highlight.value === 'No techniques yet' || highlight.value === 'No milestone yet';

    return (
        <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[120px] h-[180px] flex flex-col">
        {/* Title */}
        {highlight.type !== 'achievement' && (
          <div className="mb-2">
            <p className="text-[12px] font-semibold text-white font-roboto mb-0">{highlight.title}</p>
            <p className="text-[10px] font-light text-gray-400 font-roboto">
              this month
              {highlight.type === 'new_connections' && <span className="ml-1 text-sm">👥</span>}
            </p>
          </div>
        )}

        {/* Content based on card type */}
        <div className="flex-1 flex flex-col justify-center">
          {highlight.type === 'total_trainings' ? (
            <DonutChart
              segments={[
                {
                  label: 'Group',
                  value: highlightsData?.totalTrainings.group || 0,
                  color: '#9852DB',
                  percentage: highlightsData?.totalTrainings.total > 0 
                    ? (highlightsData.totalTrainings.group / highlightsData.totalTrainings.total) * 100 
                    : 0
                },
                {
                  label: 'Private',
                  value: highlightsData?.totalTrainings.private || 0,
                  color: '#416FF0',
                  percentage: highlightsData?.totalTrainings.total > 0 
                    ? (highlightsData.totalTrainings.private / highlightsData.totalTrainings.total) * 100 
                    : 0
                }
              ]}
              total={highlightsData?.totalTrainings.total || 0}
              size={64}
              strokeWidth={8}
              showCenterValue={true}
              showPercentages={true}
              showLegend={true}
            />
          ) : highlight.type === 'new_connections' ? (
            <MetricCard
              value={highlight.value}
              description={isEmpty ? "No new friends? It's time to expand your network!" : "You're expanding your community!"}
              isEmpty={isEmpty}
            />
        ) : highlight.type === 'most_frequent_partner' ? (
          <PartnerCard
            partner={highlight.partnerInfo}
            isEmpty={isEmpty}
            emptyMessage="Tag your partners on your trainings to discover who you roll the most"
          />
        ) : highlight.type === 'average_rating' ? (
          <MetricCard
            value={highlight.value}
            icon="⭐"
            description={isEmpty ? "Rate your trainings to see your average performance" : "Keep rating your trainings to improve! ⭐"}
            isEmpty={isEmpty}
          />
        ) : highlight.type === 'total_hours' ? (
          <MetricCard
            value={highlight.value}
            icon="🕐"
            description={isEmpty ? "Complete trainings to track your mat time" : "You're a mat lover!"}
            isEmpty={isEmpty}
          />
        ) : highlight.type === 'most_applied_technique' ? (
          <TechniqueCard
            technique={highlight.techniqueInfo}
            isEmpty={isEmpty}
            emptyMessage="Tag techniques on your trainings notes to discover which you apply more!"
          />
        ) : highlight.type === 'schedule_status' ? (
          <ScheduleStatusCard
            data={highlight.scheduleStatusData}
            isEmpty={isEmpty}
          />
        ) : highlight.type === 'achievement' ? (
          <AchievementCard
            achievement={highlight.achievementInfo}
            isEmpty={isEmpty}
          />
        ) : (
          <MetricCard
            value={highlight.value}
            icon={highlight.icon}
            description={highlight.description}
            isEmpty={isEmpty}
          />
        )}
        </div>
      </div>
    );
  };

  return (
    <div className="mb-8">
      <h2 className="text-sm font-bold text-white font-roboto mb-4">
        {t('home.journey_highlights.title')}
      </h2>
      
      <div className="relative overflow-hidden" style={{ maxWidth: 'calc(100vw - 20px)' }}>
        <div 
          className="w-full overflow-x-auto pb-2 scrollbar-hide" 
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          <div className="flex space-x-4">
            {highlights.map((highlight) => (
              <div key={highlight.id}>
                {renderCard(highlight)}
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-4 space-x-2">
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index}
                onClick={() => scrollToPage(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-white' : 'bg-gray-500'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PractitionerJourneyHighlightsSection;
